import { get, find, at } from 'lodash';
import { createSelector } from 'reselect';

import Purchase from '../lib/purchase';
import Membership from '../lib/membership';
import { getURLParams } from '../lib/functions';
import { getCurrentUser } from './user-selector';

export const getCurrentPurchase = ({ purchases }) => purchases.byId[purchases.current];

export const getCurrentProjectedPurchase = ({ projections }, namespace) => {
  return get(projections, `${namespace}.purchase`)
};

export const getMemberships = ({ purchases, memberships }) => {
  const purchase = purchases.byId[purchases.current];
  const membershipIds = get(purchase, 'memberships', []);
  return membershipIds.map(id => memberships.byId[id]).filter(m => !!m);
};

export const getProjectedMemberships = ({ purchases, memberships, projections }, namespace) => {
  const purchase = purchases.byId[purchases.current];
  const membershipIds = get(purchase, 'memberships', []);
  return membershipIds.map(id => get(projections, `${namespace}.memberships.${id}`, {}))
    .filter(m => m);
};

export const getOrganizerMembership = createSelector(
  getMemberships,
  memberships => find(memberships, member => Membership.isOrganizer(member.role)),
);

export const getConsumerFieldValues = ({ purchases, consumerFieldValues }) => {
  const purchase = purchases.byId[purchases.current];
  const valuesIds = get(purchase, 'consumerFieldValues', []);
  return at(consumerFieldValues.byId, valuesIds);
};

export const getConsumerFieldSchemas = ({ purchases, consumerFieldSchemas }) => {
  const purchase = purchases.byId[purchases.current];
  const schemaIds = get(purchase, 'consumerFieldSchemas', []);
  return at(consumerFieldSchemas.byId, schemaIds);
};

export const findCurrentUserMembership = createSelector(
  [getMemberships, getCurrentUser],
  (memberships, user) => {
    const params = getURLParams();
    const userId = user ? user.id : params.target;
    if (!userId) return null;
    const membership = find(memberships, membership => membership.user.id === userId);
    if (!user && membership && !Membership.isHoldout(membership.status)) return null;

    return membership;
  },
);

export const getCurrentUserMembership = ({ memberships }) => {
  return memberships.byId[memberships.current];
};

export const getCommittedMemberships = createSelector(
  [getMemberships],
  (memberships) => {
    return memberships.filter(({ status }) => Membership.isCommitted(status));
  },
);

export const getFailedMemberships = createSelector(
  getMemberships,
  memberships => memberships.filter(m => m.failed),
);

export const getShortMemberships = createSelector(
  getCommittedMemberships,
  memberships => memberships.filter(m => m.short),
);

export const getFailedInviteeMemberships = createSelector(
  getFailedMemberships,
  memberships => memberships.filter(m => m.role !== 'organizer'),
);

export const getHoldoutMemberships = createSelector(
  getMemberships,
  memberships => memberships.filter(({ status }) => Membership.isHoldout(status)),
);

export const ifImOrg = createSelector(
  [findCurrentUserMembership, getOrganizerMembership],
  (currentMembership, organizerMembership) => {
    return (evaluation) => {
      if (!currentMembership || !organizerMembership) return false;
      if (currentMembership.id !== organizerMembership.id) return false;

      return typeof evaluation === 'function' ? evaluation() : evaluation;
    };
  },
);

export const ifImNotOrg = createSelector(
  [ifImOrg],
  (ifOrg) => {
    return (evaluation) => {
      if (ifOrg(true)) return false;

      return typeof evaluation === 'function' ? evaluation() : evaluation;
    };
  },
);

export const getCurrentContribution = createSelector(
  [getCurrentPurchase, findCurrentUserMembership],
  (purchase, membership) => {
    return membership.currentContribution;
  },
);

export const getCurrentPurchaseMerchantName = createSelector(
  [getCurrentPurchase],
  purchase => purchase.merchant.displayName,
);

export const getCurrentPurchaseLegalDocuments = ({ purchases, legalDocuments }) => {
  const purchase = purchases.byId[purchases.current];
  const docIds = get(purchase, 'product.legalDocuments', []);
  return docIds.map(({ id }) => legalDocuments.byId[id]).filter(doc => !!doc);
};

export const getNextPaymentProjected = (state, SHARED_NAMESPACE) => {
  const purchase = getCurrentPurchase(state);
  const projectedPurchase = getCurrentProjectedPurchase(state, SHARED_NAMESPACE);
  const nextPayment = Purchase.getNextPayment(purchase);

  if (!projectedPurchase || !nextPayment) return null;

  return projectedPurchase.payments[nextPayment.number - 1];
};

export const getPurchaseConsumerFieldSchemas = createSelector(
  [getConsumerFieldSchemas],
  consumerFieldSchemas => consumerFieldSchemas.filter(field => field.scope === 'purchase'),
);

export const getRequiredPurchaseConsumerFieldSchemas = createSelector(
  [getPurchaseConsumerFieldSchemas],
  consumerFieldSchemas => consumerFieldSchemas.filter(field => field.required),
);

export const getMemberConsumerFieldSchemas = createSelector(
  [getConsumerFieldSchemas],
  consumerFieldSchemas => consumerFieldSchemas.filter(field => field.scope === 'member'),
);



// WEBPACK FOOTER //
// ./src/selectors/purchase-selector.js